<div class="page-container">
  <app-page-header
    title="Recordings"
    subtitle="Manage and review audio recordings"
    [showBackButton]="true"
    backRoute="/userhome"
    backLabel="Back to Dashboard">
    <button class="btn-primary" routerLink="/audio">
      <mat-icon aria-hidden="true">mic</mat-icon>
      Create Recording
    </button>
  </app-page-header>

  <div class="content-wrapper">

    <!-- Filters -->
    <section class="rec-toolbar" aria-label="Recording filters">
      <div class="rec-toolbar__top">
        <div class="rec-search">
          <mat-icon class="rec-search__icon" aria-hidden="true">search</mat-icon>
          <input
            class="rec-search__input"
            [(ngModel)]="search"
            (keyup.enter)="onFilterChange()"
            placeholder="Search recordings by name…"
            aria-label="Search recordings by name" />
          @if (search) {
            <button type="button" class="rec-search__clear" (click)="clearSearch()" aria-label="Clear search">
              <mat-icon aria-hidden="true">close</mat-icon>
            </button>
          }
        </div>

        <div class="rec-toolbar__controls">
          @if (showOwnerStationFilters) {
            <app-owner-station-filters [userRole]="userRole" (filterChange)="onOwnerStationFilterChange($event)" />
          }

          <mat-form-field appearance="outline" class="length-filter-field" subscriptSizing="dynamic">
            <mat-label>Length</mat-label>
            <mat-select
              [(ngModel)]="lengthFilter"
              (selectionChange)="onFilterChange()"
              panelClass="owner-station-filter-panel">
              @for (option of lengthOptions; track option.label) {
                <mat-option [value]="option.value">{{ option.label }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline" class="type-filter-field" subscriptSizing="dynamic">
            <mat-label>Type</mat-label>
            <mat-select
              [(ngModel)]="preemptibleFilter"
              (selectionChange)="onFilterChange()"
              panelClass="owner-station-filter-panel">
              <mat-option value="">All</mat-option>
              <mat-option value="true">Preemptible Promos</mat-option>
            </mat-select>
          </mat-form-field>

          <button type="button" class="btn-primary btn-sm" (click)="onFilterChange()">Apply</button>

          @if (hasActiveFilters) {
            <button type="button" class="rec-reset-btn" (click)="resetFilters()">
              <mat-icon aria-hidden="true">restart_alt</mat-icon>
              Reset
            </button>
          }
        </div>
      </div>

      <div class="rec-toolbar__bottom">
        <div class="rec-status-tabs" role="group" aria-label="Filter by approval status">
          @for (option of statusOptions; track option.label) {
            <button
              type="button"
              class="rec-status-tab"
              [class.is-active]="statusFilter === option.value"
              [attr.aria-pressed]="statusFilter === option.value"
              (click)="setStatusFilter(option.value)">
              {{ option.label }}
            </button>
          }
        </div>

        @if (!loading && totalRecordings > 0) {
          <p class="rec-count">Showing {{ rangeStart }}–{{ rangeEnd }} of {{ totalRecordings }} recordings</p>
        }
      </div>
    </section>

    <!-- Loading -->
    <div *ngIf="loading" class="recordings-grid" aria-hidden="true">
      <div *ngFor="let i of [1,2,3,4]" class="rec-card rec-card--skeleton">
        <div class="skeleton-head">
          <div class="skeleton-block skeleton-avatar"></div>
          <div class="skeleton-lines">
            <div class="skeleton-block skeleton-name"></div>
            <div class="skeleton-block skeleton-meta"></div>
          </div>
        </div>
        <div class="skeleton-block skeleton-audio"></div>
        <div class="skeleton-actions">
          <div class="skeleton-block skeleton-btn"></div>
          <div class="skeleton-block skeleton-btn"></div>
        </div>
      </div>
    </div>

    <!-- Empty -->
    <div *ngIf="!loading && recordings.length === 0" class="rec-empty">
      <span class="rec-empty__icon">
        <mat-icon aria-hidden="true">graphic_eq</mat-icon>
      </span>
      <h3 class="rec-empty__title">No recordings found</h3>
      <p class="rec-empty__text">
        {{ hasActiveFilters
          ? 'No recordings match the current filters. Try clearing them to see everything.'
          : 'Recordings you create will appear here, ready to review and approve.' }}
      </p>
      @if (hasActiveFilters) {
        <button type="button" class="btn-secondary" (click)="resetFilters()">
          <mat-icon aria-hidden="true">restart_alt</mat-icon>
          Clear filters
        </button>
      } @else {
        <button type="button" class="btn-primary" routerLink="/audio">
          <mat-icon aria-hidden="true">mic</mat-icon>
          Create Recording
        </button>
      }
    </div>

    <!-- List -->
    <ng-container *ngIf="!loading && recordings.length > 0">
      <div class="recordings-grid">
        <article
          *ngFor="let r of recordings"
          class="rec-card"
          [class.highlight-from-notification]="isHighlightedRecording(r)"
          [class.approval-dialog-active]="isApprovalDialogOpenFor(r)"
          [class.rec-card--superseded]="isSupersededRecording(r)"
          [attr.id]="'recording-item-' + recordingRowDomId(r)">

          <header class="rec-card__head">
            <span class="rec-card__icon" aria-hidden="true">
              <mat-icon>graphic_eq</mat-icon>
            </span>

            <div class="rec-card__titles">
              <h3 class="rec-card__name" [attr.title]="r.name">{{ r.name | titlecase }}</h3>
              <div class="rec-card__meta">
                <span class="rec-meta">
                  <mat-icon aria-hidden="true">tag</mat-icon>{{ r.id }}
                </span>
                <span class="rec-meta">
                  <mat-icon aria-hidden="true">schedule</mat-icon>{{ r.audiolength | number:'1.0-0' }}s
                </span>
                <span class="rec-meta" *ngIf="r.station_name">
                  <mat-icon aria-hidden="true">radio</mat-icon>{{ r.station_name }}
                </span>
              </div>
            </div>

            <div class="rec-card__badges">
              <span
                *ngIf="r.approval_status !== undefined"
                class="status-badge"
                [ngClass]="statusBadgeClass(r)">
                {{ statusBadgeLabel(r) }}
              </span>
              <span *ngIf="r.is_preemptible_promo" class="status-badge badge-preemptible">
                Preemptible
              </span>
            </div>
          </header>

          <div class="rad-player" [class.rad-player--playing]="isPlaying(r)">
            <audio
              preload="metadata"
              [src]="r.presigned_url || r.audio_url"
              (loadedmetadata)="registerAudio(r, $event)"
              (timeupdate)="onAudioTimeUpdate(r, $event)"
              (ended)="onAudioEnded(r)"
              (play)="onAudioPlay(r)"
              (pause)="onAudioPause(r)">
            </audio>

            <button
              type="button"
              class="rad-player__play"
              (click)="togglePlay(r)"
              [attr.aria-label]="isPlaying(r) ? 'Pause' : 'Play'">
              <mat-icon aria-hidden="true">{{ isPlaying(r) ? 'pause' : 'play_arrow' }}</mat-icon>
            </button>

            <div class="rad-player__body">
              <div class="rad-player__times">
                <span>{{ formatPlayerTime(currentTime(r)) }}</span>
                <span>{{ formatPlayerTime(duration(r)) }}</span>
              </div>
              <input
                type="range"
                class="rad-player__seek"
                min="0"
                [max]="duration(r) || 0"
                step="0.1"
                [value]="currentTime(r)"
                [style.--progress]="progress(r) + '%'"
                (input)="seekAudio(r, $event)"
                [attr.aria-label]="'Seek ' + (r.name || 'recording')" />
            </div>

            <button
              type="button"
              class="rad-player__icon-btn"
              (click)="toggleMute(r)"
              [attr.aria-label]="isMuted(r) ? 'Unmute' : 'Mute'">
              <mat-icon aria-hidden="true">{{ isMuted(r) ? 'volume_off' : 'volume_up' }}</mat-icon>
            </button>

            <div class="rad-player__speed">
              <button
                type="button"
                class="rad-player__speed-btn"
                (click)="toggleSpeedMenu(r, $event)"
                [attr.aria-expanded]="isSpeedMenuOpen(r)"
                aria-haspopup="menu"
                [attr.aria-label]="'Playback speed ' + playbackRate(r) + 'x'">
                {{ playbackRate(r) }}x
              </button>
              @if (isSpeedMenuOpen(r)) {
                <div class="rad-player__speed-menu" role="menu">
                  @for (speed of playbackSpeeds; track speed) {
                    <button
                      type="button"
                      class="rad-player__speed-option"
                      role="menuitemradio"
                      [class.is-active]="playbackRate(r) === speed"
                      (click)="setPlaybackSpeed(r, speed, $event)">
                      {{ speed }}x
                    </button>
                  }
                </div>
              }
            </div>
          </div>

          @if (r.product_types?.length) {
            <div class="product-type-chips">
              @for (pt of r.product_types; track pt.id) {
                <span class="product-type-chip">{{ productTypeLabel(pt) }}</span>
              }
            </div>
          }

          <footer class="rec-card__actions">
            <ng-container *ngIf="canApproveRecording(r) && !isApprovalDialogOpenFor(r)">
              <button type="button" class="btn-primary btn-sm" (click)="approve(r)">
                <mat-icon aria-hidden="true">check</mat-icon>
                Approve
              </button>

              <button type="button" class="btn-secondary btn-sm" (click)="decline(r)">
                <mat-icon aria-hidden="true">block</mat-icon>
                Decline
              </button>
            </ng-container>

            @if (isApprovalDialogOpenFor(r)) {
              <div class="row-approval-active" role="status" aria-live="polite">
                <mat-spinner diameter="18" class="row-approval-spinner"></mat-spinner>
                <span>Assigning product types…</span>
              </div>
            }

            @if (isSupersededRecording(r) && !isApprovalDialogOpenFor(r)) {
              <p class="rec-superseded-note">Replaced by a corrected recording.</p>
            }

            @if (canViewFeedback(r) && !isApprovalDialogOpenFor(r)) {
              <button type="button" class="btn-secondary btn-sm" (click)="openFeedback(r)">
                <mat-icon aria-hidden="true">feedback</mat-icon>
                View Feedback
              </button>
            }

            @if (canEditRecording(r) && !isApprovalDialogOpenFor(r)) {
              <button type="button" class="btn-secondary btn-sm" (click)="openEdit(r)">
                <mat-icon aria-hidden="true">edit</mat-icon>
                Edit
              </button>
            }

            @if (canDownloadRecording(r) && !isApprovalDialogOpenFor(r)) {
              <button
                type="button"
                class="btn-secondary btn-sm"
                (click)="downloadRecording(r)"
                [disabled]="downloadingId === recordingRowDomId(r)">
                @if (downloadingId === recordingRowDomId(r)) {
                  <span class="btn-spinner-dark"></span>
                } @else {
                  <mat-icon aria-hidden="true">download</mat-icon>
                }
                Download
              </button>
            }

            @if (canDeleteRecording(r) && !isApprovalDialogOpenFor(r)) {
              <button type="button" class="btn-danger-outline btn-sm" (click)="confirmDelete(r)">
                <mat-icon aria-hidden="true">delete</mat-icon>
                Delete
              </button>
            }
          </footer>
        </article>
      </div>

      <mat-paginator
        [length]="totalRecordings"
        [pageSize]="pageSize"
        [pageSizeOptions]="[5, 10, 25, 100]"
        [pageIndex]="pageIndex"
        (page)="onPageChange($event)"
        aria-label="Select page">
      </mat-paginator>
    </ng-container>

  </div>
</div>
